import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './Auth.css';
import './Game.css';
import './Wiki.css';
import AuthLayeredCard from './AuthLayeredCard';
import AuthSelectionToolbar from './AuthSelectionToolbar';
import { authButtonStyle } from '../constants/authStyles';
import { useGameConfig } from '../context/GameConfigContext';
import WikiMonsterPopup from './wiki/WikiMonsterPopup';
import WikiItemDetail from './wiki/WikiItemDetail';
import WikiEfsunPanel from './wiki/WikiEfsunPanel';
import WikiBattleSprite from './wiki/WikiBattleSprite';
import WikiSocketPanel from './wiki/WikiSocketPanel';
import { tl } from '../utils/inlineI18n';

const TABS = ['villages', 'items', 'efsun', 'sockets'];

const Wiki = () => {
  const { t } = useTranslation('wiki');
  const { t: tc } = useTranslation('common');
  const { loaded } = useGameConfig();
  const navigate = useNavigate();

  const [tab, setTab] = useState('villages');
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [villages, setVillages] = useState([]);
  const [selectedVillageId, setSelectedVillageId] = useState(null);
  const [monsters, setMonsters] = useState([]);
  const [selectedMonster, setSelectedMonster] = useState(null);

  const [efsunProfiles, setEfsunProfiles] = useState([]);
  const [efsunLoading, setEfsunLoading] = useState(false);

  useEffect(() => {
    const username = localStorage.getItem('username');
    if (!username) {
      navigate('/login');
      return;
    }
  }, [navigate]);

  const loadVillages = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/api/wiki/villages');
      if (res.data?.success) {
        const list = res.data.villages || [];
        setVillages(list);
        setSelectedVillageId((prev) => {
          if (prev && list.some((v) => v.id === prev)) return prev;
          return list[0]?.id ?? null;
        });
      } else {
        setError(t('loadError'));
      }
    } catch {
      setError(t('loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadMonsters = useCallback(async (villageId) => {
    if (!villageId) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/api/wiki/monsters', { params: { villageId } });
      if (res.data?.success) {
        setMonsters(res.data.monsters || []);
      } else {
        setError(t('loadError'));
      }
    } catch {
      setError(t('loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadEfsun = useCallback(async () => {
    setEfsunLoading(true);
    try {
      const res = await axios.get('/api/wiki/efsun');
      if (res.data?.success) {
        setEfsunProfiles(res.data.profiles || []);
      }
    } catch {
      setEfsunProfiles([]);
    } finally {
      setEfsunLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!loaded) return;
    loadVillages();
    loadEfsun();
  }, [loaded, loadVillages, loadEfsun, refreshKey]);

  useEffect(() => {
    if (!loaded || !selectedVillageId) return;
    loadMonsters(selectedVillageId);
  }, [loaded, selectedVillageId, loadMonsters, refreshKey]);

  const handleRefresh = () => setRefreshKey((k) => k + 1);

  const kindLabel = (kind) => {
    if (kind === 'metin') return t('kindMetin');
    if (kind === 'boss') return t('kindBoss');
    return t('kindMonster');
  };

  const getMonsterSprite = (monster) => {
    if (!monster?.battleSprites) return null;
    const bs = typeof monster.battleSprites === 'string'
      ? JSON.parse(monster.battleSprites)
      : monster.battleSprites;
    return bs?.run || bs?.all || bs?.attack || null;
  };

  if (!loaded) {
    return (
      <div className="auth-container auth-container--selection wiki-page">
        <AuthLayeredCard className="wiki-card">
          <p>{t('loading')}</p>
        </AuthLayeredCard>
      </div>
    );
  }


  return (
    <div className="auth-container auth-container--selection wiki-page">
      <AuthLayeredCard className="wiki-card">
        <AuthSelectionToolbar
          onBack={() => navigate('/game')}
          backLabel={tc('back')}
          buttonStyle={authButtonStyle}
          title={t('title')}
          subtitle={t('subtitle')}
        />

        <div className="wiki-toolbar">
          <div className="wiki-tabs">
            {TABS.map((tabKey) => (
              <button
                key={tabKey}
                type="button"
                className={`wiki-tab ${tab === tabKey ? 'wiki-tab--active' : ''}`}
                onClick={() => setTab(tabKey)}
              >
                {tabKey === 'villages' ? t('tabVillages') : tabKey === 'items' ? t('tabItems') : tabKey === 'sockets' ? tl('💎 Soket & Taşlar', '💎 Sockets & Stones') : t('tabEfsun')}
              </button>
            ))}
          </div>
          <button type="button" className="wiki-refresh-btn" onClick={handleRefresh} style={authButtonStyle}>
            {t('refresh')}
          </button>
        </div>

        {error ? <p className="wiki-error">{error}</p> : null}
        {loading && tab === 'villages' ? <p>{t('loading')}</p> : null}

        {tab === 'villages' ? (
          <div className="wiki-villages-layout">
            <div className="wiki-village-list">
              <h4>{t('selectVillage')}</h4>
              {villages.map((village) => (
                <button
                  key={village.id}
                  type="button"
                  className={`wiki-village-row ${selectedVillageId === village.id ? 'wiki-village-row--active' : ''}${village.locked ? ' wiki-locked' : ''}`}
                  onClick={() => setSelectedVillageId(village.id)}
                >
                  {village.locked ? (
                    <span className="wiki-village-thumb wiki-locked-thumb" aria-hidden="true">?</span>
                  ) : village.imageUrl ? (
                    <img src={`/${village.imageUrl.replace(/^\//, '')}`} alt={village.name} className="wiki-village-thumb" />
                  ) : null}
                  <div className="wiki-village-info">
                    <strong>{village.name}</strong>
                    <span>{t('villageLevel', { level: village.level })}</span>
                    {village.locked ? (
                      <span>{t('lockedHint')}</span>
                    ) : (
                      <span>{t('monsterCount', { count: village.monsterCount })} · {t('metinCount', { count: village.metinCount })} · {t('bossCount', { count: village.bossCount })}</span>
                    )}
                  </div>
                </button>
              ))}
            </div>

            <div className="wiki-monster-grid">
              {monsters.map((monster) => {
                const sprite = getMonsterSprite(monster);
                return (
                  <button
                    key={`${monster.kind}-${monster.id}`}
                    type="button"
                    className={`wiki-monster-card wiki-monster-card--${monster.kind}${monster.locked ? ' wiki-locked' : ''}`}
                    onClick={() => !monster.locked && setSelectedMonster(monster)}
                    aria-disabled={monster.locked || undefined}
                  >
                    <span className="wiki-kind-badge">{kindLabel(monster.kind)}</span>
                    {sprite ? (
                      <WikiBattleSprite
                        sheet={sprite}
                        playing
                        loop
                        className="wiki-monster-card-sprite"
                        fallbackLabel={monster.name}
                      />
                    ) : (
                      <span className="wiki-monster-card-placeholder">?</span>
                    )}
                    <span className="wiki-monster-card-name">{monster.name}</span>
                    {!monster.locked ? <span className="wiki-monster-card-level">Lv.{monster.level}</span> : null}
                    {monster.drops?.length > 0 ? (
                      <span className="wiki-monster-card-drops">{monster.drops.length} drop</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        {tab === 'items' ? (
          <WikiItemDetail efsunProfiles={efsunProfiles} refreshKey={refreshKey} />
        ) : null}

        {tab === 'sockets' ? <WikiSocketPanel /> : null}

        {tab === 'efsun' ? (
          <WikiEfsunPanel profiles={efsunProfiles} loading={efsunLoading} />
        ) : null}
      </AuthLayeredCard>

      {selectedMonster ? (
        <WikiMonsterPopup monster={selectedMonster} onClose={() => setSelectedMonster(null)} />
      ) : null}
    </div>
  );
};

export default Wiki;
